import * as React from 'react'
import { FC, useCallback } from 'react'
import { Formik } from 'formik'
import * as yup from 'yup'

import { EditLeadNameFormFields } from './EditLeadNameFormFields'
import { ILeadDetail } from '@user/store/lead/typings'
import { toast } from '@sales-center/components'
import { updateName } from '@user/store/lead/api'
import { useAppDispatch } from '@user/store/hooks/useAppDispatch'

interface Props {
  detail: ILeadDetail
  onClose: () => void
}

export interface EditLeadNameFormModel {
  company: string
  firstName: string
  lastName: string
}

const validationSchema = yup.object({
  company: yup.string().test((value, context) =>
    !value && !context.parent.firstName && !context.parent.lastName
      ? context.createError({
          message: 'Required',
          path: 'company',
        })
      : true,
  ),
  firstName: yup.string().test((value, context) =>
    !value && !context.parent.company
      ? context.createError({
          message: 'Required',
          path: 'firstName',
        })
      : true,
  ),
  lastName: yup.string().test((value, context) =>
    !value && !context.parent.company
      ? context.createError({
          message: 'Required',
          path: 'lastName',
        })
      : true,
  ),
})

export const EditLeadNameFormWrapper: FC<Props> = (props) => {
  const { detail, onClose } = props
  const dispatch = useAppDispatch()

  const onSubmit = useCallback(
    async (model: EditLeadNameFormModel) => {
      const input = {
        id: detail.id,
        contactId: detail.primaryContact.id,
        company: model.company,
        firstName: model.firstName,
        lastName: model.lastName,
      }
      const response = await dispatch(updateName(input))

      if (updateName.fulfilled.match(response)) {
        toast.success('Lead name updated.')
        // Display the additional toast message if any Draft agreements were updated
        if (response.payload.draftAgreementsUpdated) {
          toast.success(
            'Contact information has been updated on any draft agreements.',
          )
        }
        onClose()
      } else {
        toast.error('Failed to update lead name.')
      }
    },
    [detail.id, detail.primaryContact.id, dispatch, onClose],
  )

  return (
    <Formik
      initialValues={{
        company: detail.primaryContact.company,
        firstName: detail.primaryContact.firstName,
        lastName: detail.primaryContact.lastName,
      }}
      onSubmit={onSubmit}
      validationSchema={validationSchema}
    >
      <EditLeadNameFormFields onCancel={onClose} />
    </Formik>
  )
}

EditLeadNameFormWrapper.displayName = 'EditLeadNameFormWrapper'
